import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ControlPointService } from '../../../shared/services/control-points/control-point.service';

type CheckinState =
  | 'submitting'
  | 'success'
  | 'rejected'
  | 'forbidden'
  | 'unknown'
  | 'error';

@Component({
  selector: 'app-control-point-checkin',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    CardModule,
    ButtonModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './checkin-page.component.html',
  styleUrl: './checkin-page.component.scss',
})
export class CheckinPageComponent implements OnInit {
  #service = inject(ControlPointService);
  #route = inject(ActivatedRoute);

  /** The NFC tag UID resolved from the route. */
  tag = signal('');
  state = signal<CheckinState>('submitting');
  /** Missing qualification names for the rejected (403) state. */
  missingQualifications = signal<string[]>([]);
  /** Check-in timestamp (ISO, UTC) for the success state. */
  checkedInAt = signal<string | null>(null);
  /** Control point name for the success state (falls back to the tag UID). */
  controlPointName = signal<string | null>(null);

  /** In-flight latch: prevents a double submit on slow mobile connections. */
  #inFlight = false;

  ngOnInit(): void {
    this.tag.set(this.#route.snapshot.paramMap.get('tag') ?? '');
    this.#submit();
  }

  /** Re-fires the POST after a connectivity/server failure. */
  retry(): void {
    this.#submit();
  }

  /** Fires the check-in POST exactly once at a time. */
  #submit(): void {
    if (this.#inFlight) {
      return;
    }
    this.#inFlight = true;
    this.state.set('submitting');
    this.missingQualifications.set([]);

    this.#service.checkin(this.tag()).subscribe({
      next: (response) => {
        this.#inFlight = false;
        this.checkedInAt.set(response.data?.checked_in_at ?? null);
        this.controlPointName.set(response.data?.control_point_name ?? null);
        this.state.set('success');
      },
      error: (err: unknown) => {
        this.#inFlight = false;
        this.#handleError(err);
      },
    });
  }

  #handleError(err: unknown): void {
    if (!(err instanceof HttpErrorResponse)) {
      this.state.set('error');
      return;
    }

    switch (err.status) {
      case 403: {
        const body = err.error as
          | { error?: string; missing?: string[] }
          | null
          | undefined;
        if (body?.error === 'missing_qualifications') {
          this.missingQualifications.set(
            Array.isArray(body.missing) ? body.missing : [],
          );
          this.state.set('rejected');
        } else {
          // Plain 403: the user lacks the controlpoints.checkin permission.
          this.state.set('forbidden');
        }
        break;
      }
      case 404:
        this.state.set('unknown');
        break;
      default:
        // 0 (network), 5xx, or any other unexpected status: retryable.
        this.state.set('error');
        break;
    }
  }
}
